import { useState, useRef, useEffect } from "react";
import { GlobeAltIcon, MapPinIcon } from "@heroicons/react/24/outline";
import imagenotfound from "../../images/noimagefoundsquare.jpg";
import { useTranslation } from "react-i18next";

export default function CompanyCard({ company, onClick }) {
  const {
    name,
    imageUrl,
    website,
    address,
    twitchUrl,
    spotifyUrl,
    youtubeUrl,
  } = company;

  const [fitMode, setFitMode] = useState("cover");
  const imgRef = useRef(null);
  const { t } = useTranslation();

  const googleMapsLink = address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        address
      )}`
    : null;

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    const handleLoad = () => {
      const { naturalWidth, naturalHeight } = img;
      const ratio = naturalWidth / naturalHeight;

      // Détection : carré = cover, rectangle horizontal ou vertical = contain
      if (ratio > 1.2 || ratio < 0.8) {
        setFitMode("contain");
      } else {
        setFitMode("cover");
      }
    };

    if (img.complete) {
      handleLoad();
    } else {
      img.addEventListener("load", handleLoad);
    }

    return () => {
      img.removeEventListener("load", handleLoad);
    };
  }, [imageUrl]);

  const normalizeUrl = (inputUrl) => {
    if (!inputUrl) return null;

    let url = inputUrl.trim();

    // Supprimer les slashs finaux multiples
    url = url.replace(/\/+$/, "");

    // Corrige le casing et supprime les espaces internes
    url = url.replace(/\s/g, "").toLowerCase();

    // Ajoute https:// si absent
    if (!/^https?:\/\//.test(url)) {
      url = `https://${url}`;
    }

    try {
      return new URL(url).href;
    } catch (e) {
      console.warn("Invalid URL:", inputUrl);
      return null;
    }
  };

  const openInNewTab = (url) => {
    const cleanedUrl = normalizeUrl(url);
    if (cleanedUrl) {
      window.open(cleanedUrl, "_blank");
    } else {
      console.warn("Lien non valide :", url);
    }
  };

  return (
    <div
      onClick={onClick}
      className="flex items-stretch h-36 border rounded-lg cursor-pointer transition-transform duration-300 shadow-sm hover:shadow-lg hover:scale-105 bg-white text-primary"
    >
      {/* Image */}
      <div className="w-36 aspect-[4/3] overflow-hidden rounded-l-lg flex-shrink-0 bg-white flex items-center justify-center">
        <img
          ref={imgRef}
          src={imageUrl || imagenotfound}
          alt={name}
          className="max-h-full max-w-full object-contain"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = imagenotfound;
          }}
          loading="lazy"
        />
      </div>

      {/* Content */}
      <div className="flex flex-col gap-1  text-sm p-4">
        <h3 className="font-bold text-md ">{name}</h3>
        <div className="flex flex-col gap-2 mt-2 text-gray-500 text-sm">
          {googleMapsLink && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                openInNewTab(googleMapsLink);
              }}
              className="flex items-center gap-1 hover:text-primary"
              title="Adresse"
            >
              <MapPinIcon className="w-5 h-5" />
              <span>{t("global.address")}</span>
            </button>
          )}
        </div>

        <div className="flex flex-col gap-2 mt-1 text-gray-500 text-sm">
          {website && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                openInNewTab(website);
              }}
              className="flex items-center gap-1 hover:text-primary"
              title="Site Web"
            >
              <GlobeAltIcon className="w-5 h-5" />
              <span>{t("becomeProvider.websiteLabel")}</span>
            </button>
          )}

          {twitchUrl && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                openInNewTab(twitchUrl);
              }}
              className="flex items-center gap-1 hover:text-purple-600"
              title="Twitch"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M4 2L2 6v14h6v2h4l2-2h4l4-4V2H4zm16 12l-2 2h-4l-2 2v-2H6V4h14v10z" />
                <path d="M15 7h-2v5h2V7zm4 0h-2v5h2V7z" />
              </svg>
              <span>{t("global.twitch")}</span>
            </button>
          )}

          {spotifyUrl && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                openInNewTab(spotifyUrl);
              }}
              className="flex items-center gap-1 hover:text-green-500"
              title="Spotify"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0a12 12 0 100 24A12 12 0 0012 0zm5.25 17.7a.75.75 0 01-1.05.2c-2.88-1.77-6.49-2.17-10.77-1.18a.75.75 0 01-.33-1.46c4.7-1.07 8.78-.62 12.05 1.33a.75.75 0 01.2 1.05zm1.5-3.15a.9.9 0 01-1.26.24c-3.3-2.05-8.33-2.65-12.23-1.43a.9.9 0 11-.52-1.73c4.44-1.34 10.02-.7 13.73 1.65a.9.9 0 01.28 1.27zm.3-3.45c-3.88-2.3-10.32-2.52-14.1-1.37a1.05 1.05 0 11-.6-2.01c4.36-1.31 11.48-1.06 15.87 1.5a1.05 1.05 0 11-1.17 1.88z" />
              </svg>
              <span>{t("global.spotify")}</span>
            </button>
          )}

          {youtubeUrl && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                openInNewTab(youtubeUrl);
              }}
              className="flex items-center gap-1 hover:text-red-600"
              title="YouTube"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a2.951 2.951 0 00-2.077-2.088C19.64 3.6 12 3.6 12 3.6s-7.64 0-9.421.498a2.95 2.95 0 00-2.077 2.088C0 7.974 0 12 0 12s0 4.026.502 5.814a2.951 2.951 0 002.077 2.088C4.36 20.4 12 20.4 12 20.4s7.64 0 9.421-.498a2.95 2.95 0 002.077-2.088C24 16.026 24 12 24 12s0-4.026-.502-5.814zM9.75 15.568V8.432L15.818 12 9.75 15.568z" />
              </svg>
              <span>{t("global.youtube")}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
